import axios from 'axios';
import router from '@/router';
import store from '@/store';
import config from '@/config';

const MONOLITO_API = config.MONOLITO_API;



// Crear instancia de axios con configuración base
const apiMonolito = axios.create({
  baseURL: MONOLITO_API,
  timeout: 30000, // 30 segundos de timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

// Función para verificar si el token está expirado
function isTokenExpired(token) {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    const timeUntilExpiry = payload.exp - now;

    // Considerar expirado si faltan menos de 30 segundos
    return timeUntilExpiry < 30;
  } catch {
    return true;
  }
}

// Función para limpiar tokens expirados
// Si el access_token expiró pero hay refresh_token válido, intenta renovar en lugar de hacer logout.
let _refreshPromise = null;
function clearExpiredTokens() {
  const token = getAccessToken();
  if (!token || !isTokenExpired(token)) return;

  const refreshToken = getRefreshToken();
  if (refreshToken && isRefreshTokenValid()) {
    // Hay refresh token válido: no cerrar sesión, dejar que el interceptor de respuesta (401) lo renueve.
    // Solo eliminar el access_token vencido para que el interceptor de request no lo adjunte.
    console.warn('🔄 Access token expirado, se usará refresh token en la próxima petición.');
    localStorage.removeItem('access_token');
    store.commit('set_credentials', {
      ...store.state.credentials,
      access_token: ''
    });
    return;
  }

  // Sin refresh token: cerrar sesión
  console.warn('🗑️ Token expirado y sin refresh token, cerrando sesión...');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_payload');
  store.dispatch('clearAuth');
}

// Función para obtener el token de acceso
function getAccessToken() {
  try {
    const storeToken = store.getters.accessToken;
    const localToken = localStorage.getItem('access_token');
    const token = storeToken || localToken;

    return token;
  } catch {
    const token = localStorage.getItem('access_token');
    return token;
  }
}

// Función para verificar si el token actual es válido (no expirado)
function isTokenValid() {
  const token = getAccessToken();
  if (!token) return false;
  return !isTokenExpired(token);
}

// Función para verificar si el refresh token es válido
function isRefreshTokenValid() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  // Los refresh tokens generalmente son UUIDs o tienen formato específico
  // Verificar que no esté vacío y tenga un formato razonable
  return refreshToken.length > 10 && !refreshToken.includes('undefined');
}

// Función para obtener el refresh token
function getRefreshToken() {
  try {
    return store.getters.refreshToken || localStorage.getItem('refresh_token');
  } catch {
    return localStorage.getItem('refresh_token');
  }
}

// Función para limpiar tokens y redirigir al login
// Función para hacer logout y limpiar auth
function clearAuthAndRedirect() {
  console.warn('🚪 Limpiando autenticación y redirigiendo a login...');

  // Limpiar localStorage completamente
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_payload');
  localStorage.removeItem('user_profile');
  localStorage.removeItem('app_id');
  localStorage.removeItem('tenant_id');

  // Limpiar store
  store.dispatch('clearAuth');

  // Redirigir a login después de un pequeño delay
  setTimeout(() => {
    if (router.currentRoute.path !== '/login') {
      router.push('/login').catch(err => {
        // Ignore NavigationDuplicated error intentionally
        if (err.name !== 'NavigationDuplicated' && !err.message.includes('Avoided redundant navigation')) {
          console.error('Router error:', err);
        }
      });
    }
  }, 100);
}

// Endpoints públicos que no requieren autenticación
const PUBLIC_ENDPOINTS = [
  '/profesional',        // Lista de profesionales para turnos públicos
  '/appointments',       // Turnos disponibles para público
  '/register',          // Registro de nuevos usuarios
  '/verify',             // Verificación de códigos
  '/especialidades',     // Lista de especialidades
  '/data',               // Datos generales de configuración
  '/obras-sociales',     // Lista de obras sociales (preventivo)
  '/appointmentsdr',     // Turnos de doctor (público para visualización)
  '/appointmentsdr-historial', // Historial de turnos (público o protegido, asumiendo público por ahora)
  '/sendcode'           // Verificación WhatsApp (PATCH para enviar, PUT para validar)
];

// Endpoints públicos para métodos específicos (POST durante registro)
const PUBLIC_POST_ENDPOINTS = [
  '/people',            // Crear perfil durante registro de nuevo usuario (ej: /people/email@example.com)
  '/person'             // Alias para crear perfil (Primary endpoint según UnifiedPerson)
];

// Endpoints públicos para GET (verificación de existencia)
const PUBLIC_GET_ENDPOINTS = [
  '/person'             // Verificar si existe paciente por email (ej: /person/email@example.com)
];

// Función para verificar si una URL es un endpoint público
function isPublicEndpoint(url, method) {
  if (PUBLIC_ENDPOINTS.some(endpoint => url && url.includes(endpoint))) return true;

  // Excepción para eventos (turnos): GET es público (ver disponibilidad), DELETE/PUT/POST requieren auth
  if (url && url.includes('/events/') && method && method.toLowerCase() === 'get') {
    return true;
  }

  // Excepción para crear perfil durante registro: POST a /people es público
  const methodLower = method ? method.toLowerCase() : '';
  if (url && methodLower === 'post' &&
    PUBLIC_POST_ENDPOINTS.some(endpoint => url.includes(endpoint))) {
    return true;
  }

  // Excepción para verificar existencia de paciente: GET a /person es público
  if (url && methodLower === 'get' &&
    PUBLIC_GET_ENDPOINTS.some(endpoint => url.includes(endpoint))) {
    return true;
  }

  return false;
}

// Interceptor de request - Agregar token de autorización
apiMonolito.interceptors.request.use(
  (config) => {
    // Verificar y limpiar tokens expirados
    clearExpiredTokens();

    // Superusuario viendo como médico/paciente: el backend usa la identidad de
    // X-View-As y solo permite lecturas; se cortan acá para avisar sin llamar.
    // Las búsquedas del propio superusuario (/superuser/...) no van "como" nadie.
    const viewAsEmail = store.getters.viewAsEmail;
    if (viewAsEmail && !(config.url || '').startsWith('/superuser/')) {
      const method = (config.method || 'get').toLowerCase();
      if (!['get', 'head', 'options'].includes(method)) {
        store.commit('showSnackbar', {
          text: 'Modo solo lectura: estás viendo como otro usuario',
          color: 'warning',
          timeout: 4000
        });
        return Promise.reject(new Error('Modo solo lectura (ver como): ' + config.url));
      }
      config.headers['X-View-As'] = viewAsEmail;
    }

    const token = getAccessToken();
    const isPublic = isPublicEndpoint(config.url, config.method);

    // SIEMPRE agregar token si existe, incluso para endpoints "públicos"
    // El backend puede requerir autenticación aunque el frontend lo considere público
    if (token) {
      config.headers.Authorization = 'Bearer ' + token;

      // Verificar si el token está próximo a expirar
      if (isTokenExpiringSoon()) {
        // Token próximo a expirar en menos de 5 minutos
      }
      return config;
    }

    // Si NO hay token disponible:
    // - Para endpoints públicos: permitir sin token
    // - Para endpoints privados: redirigir a login
    if (isPublic) {
      return config;
    }

    // Para endpoints privados sin token: si hay refresh token válido, dejar pasar la request.
    // El interceptor de respuesta capturará el 401 y hará el refresh automáticamente.
    if (isRefreshTokenValid()) {
      console.warn('⚠️ Sin access token pero hay refresh token válido, se intentará renovar al recibir 401.');
      return config;
    }

    // Sin ningún token: verificar si el usuario tiene perfil guardado
    const userProfile = localStorage.getItem('user_profile');
    if (!userProfile) {
      console.warn('⚠️ Usuario no autenticado - sin perfil ni token');
      console.warn('⚠️ Redirigiendo a login...');
      clearAuthAndRedirect();
      return Promise.reject(new Error('Usuario no autenticado para: ' + config.url));
    }

    console.warn('⚠️ No hay token disponible para la petición');
    return config;
  },
  (error) => {
    console.error('Error en interceptor request:', error);
    return Promise.reject(error);
  }
);

// Interceptor de respuesta - Manejar errores de autenticación
apiMonolito.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle CORS errors
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.error('Error de red/CORS:', error.message);
      console.warn('El servidor backend no está disponible o hay un problema de CORS');
      return Promise.reject(error);
    }

    // Si es error 401, verificar si el token es reciente antes de intentar refresh
    if (error.response && error.response.status === 401 && !originalRequest._retry) {

      // Si es un endpoint público que devuelve 401, el problema ES del BACKEND si NO enviamos token.
      // Pero si enviamos token, puede que haya expirado, así que debemos permitir el refresh.
      const requestMethod = originalRequest.method || '';
      const requestUrl = originalRequest.url || '';
      const hasAuthHeader = !!originalRequest.headers?.Authorization;

      if (isPublicEndpoint(requestUrl, requestMethod) && !hasAuthHeader) {
        console.error('🚨 Endpoint PÚBLICO (sin token) devuelve 401 - El backend está requiriendo autenticación incorrectamente');
        console.error('🚨 URL:', requestUrl, 'Método:', requestMethod);

        // NO hacer refresh, retornar el error original
        return Promise.reject(error);
      }

      // SPECIAL CASE: /historia-clinica/ endpoint sometimes returns 401 for permission issues
      // instead of 403. Si el token sigue vigente, evitamos el refresh para no entrar en loop de logout.
      // Pero si el access token venció (o no hay), el 401 es de sesión: se sigue al refresh de abajo
      // (solo se reintenta una vez por el flag _retry).
      if (requestUrl.includes('/historia-clinica/') && isTokenValid()) {
        console.warn('⚠️ 401 on historia-clinica endpoint con token vigente. Assuming permissions issue, skipping token refresh.');
        return Promise.reject(error);
      }

      // Verificar si el token es válido y reciente (menos de 5 minutos de antigüedad)
      const currentToken = getAccessToken();
      if (currentToken && isTokenValid()) {
        try {
          const payload = JSON.parse(atob(currentToken.split('.')[1]));
          const tokenAge = Date.now() / 1000 - payload.iat; // edad en segundos

          if (tokenAge < 300) { // Token tiene menos de 5 minutos
            console.error('🚨 Token válido y reciente pero el monolito devuelve 401');
            console.error('🚨 Esto indica que el BACKEND MONOLITO no está validando correctamente los tokens JWT del Auth Services');
            console.error('🚨 Token age:', Math.round(tokenAge), 'segundos');
            console.error('🚨 Token payload:', payload);
            console.error('🚨 NO se hará refresh ni logout - el problema es del backend');

            // NO hacer refresh NI limpiar sesión, el problema es del backend
            // Lanzar error pero mantener la sesión activa
            const backendError = new Error('Backend monolito no acepta JWT. El token es válido pero el servidor lo rechaza.');
            backendError.isBackendAuthError = true;
            backendError.originalError = error;
            backendError.keepSession = true; // Flag para no limpiar sesión
            return Promise.reject(backendError);
          }
        } catch (e) {
          console.warn('⚠️ No se pudo decodificar el token para verificar antigüedad, se intentará refresh');
        }
      }

      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        const currentAccessToken = getAccessToken();

        if (!refreshToken || !isRefreshTokenValid()) {
          throw new Error('No refresh token available');
        }

        // Intentar refresh del token
        // Intentar refresh del token
        // Usar config para asegurar defaults si env endpoint no está definido
        const authUrl = config.AUTH_API || process.env.VUE_APP_AUTH_API;
        const appId = config.APP_ID || process.env.VUE_APP_AUTH_APP_ID;
        const tenantId = config.TENANT_ID || process.env.VUE_APP_AUTH_TENANT_ID;

        const response = await axios.post(`${authUrl}/sessions/refresh`, {
          refresh_token: refreshToken,
          app_id: appId,
          tenant_id: tenantId
        });

        const { access_token, refresh_token } = response.data;

        // Validar que los nuevos tokens sean válidos
        if (!access_token || isTokenExpired(access_token)) {
          throw new Error('Nuevo token inválido recibido del servidor');
        }

        // Guardar nuevos tokens
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);

        // Actualizar store también
        // Actualizar store también
        store.commit('set_credentials', {
          ...store.state.credentials,
          access_token,
          refresh_token
        });

        // Reintentar la request original con el nuevo token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        // IMPORTANTE: Hacer la llamada directamente sin pasar por el interceptor de request otra vez
        const retryResponse = await axios(originalRequest);
        return retryResponse;

      } catch (refreshError) {

        // Si el refresh falla, limpiar auth solo si NO es error del backend
        console.warn('⚠️ Refresh falló');
        
        // Si es error del backend con token válido, NO limpiar sesión
        if (refreshError.isBackendAuthError || refreshError.keepSession) {
          console.warn('⚠️ Error es del backend, se mantiene la sesión');
          return Promise.reject(refreshError);
        }
        
        console.warn('⚠️ Limpiando auth y redirigiendo...');
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    // Si ya intentamos refresh o es otro error 401 (evitar loop infinito)
    if (error.response?.status === 401 && originalRequest._retry) {
      console.error('🚨 Error 401 después de refresh - posible problema de autenticación en el backend');
      
      // Si es error del backend, NO limpiar sesión
      if (error.isBackendAuthError || error.keepSession) {
        console.warn('⚠️ Error es del backend, se mantiene la sesión');
        return Promise.reject(error);
      }
      
      console.warn('⚠️ Limpiando auth debido a 401 persistente...');
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // Para otros errores, mostrar mensaje apropiado
    if (error.response && error.response.status === 403) {
      console.error('Acceso denegado');
    }

    if (error.response && error.response.status >= 500) {
      console.error('Error del servidor monolito');
    }

    return Promise.reject(error);
  }
);

// Función para hacer logout
export function logout() {
  clearAuthAndRedirect();
}

// Función para verificar si el usuario está autenticado
export function isAuthenticated() {
  const token = getAccessToken();
  return !!token && store.state.authenticated;
}

// Función para verificar si el token está próximo a expirar
export function isTokenExpiringSoon() {
  const token = getAccessToken();
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    const timeUntilExpiry = payload.exp - now;

    // Considerar expirando si faltan menos de 5 minutos
    return timeUntilExpiry < 300;
  } catch {
    return true;
  }
}

export default apiMonolito; 